import { computed, onScopeDispose, shallowReactive, watch } from 'vue'
import { defineStore } from 'pinia'
import {
  disableBodyScroll,
  enableBodyScroll,
} from 'body-scroll-lock'

/**
 * ロックの種類
 * @description manual: 呼び出し側が取得・解除する（処理型）。conditional: 条件に連動して取得・解除される（状態型）
 */
type ScreenLockType = 'manual' | 'conditional'

interface ActiveLock {
  option: Required<ScreenLockOption>,
  type: ScreenLockType,
}

/**
 * 画面ロック
 * @description
 *  ロックは取得した所有者ごとに保持し、全員が手放したときだけ画面が解除される。
 *  コンポーネントからは直接使わず composables/useScreenLock.ts を経由すること（使い方は docs/screenLock.md）。
 */
export const useScreenLockStore = defineStore('screenLock', () => {
  /**
   * 現在掛かっているロック（キーは所有者ごとに発行する Symbol）
   */
  const activeLocks = shallowReactive(new Map<symbol, ActiveLock>())

  /**
   * 表示効果はロックごとに異なるため、項目ごとに OR で合成する
   * @description 後勝ちにすると、上に重なったロックが下のロックの見た目を消してしまう（モーダル内の API 通信で暗転が消える等）
   */
  const hasAny = (key: keyof ScreenLockOption): boolean => {
    return [...activeLocks.values()].some(lock => lock.option[key])
  }

  const isLock = computed<boolean>(() => activeLocks.size > 0)
  const isBlackOut = computed<boolean>(() => hasAny('hasBlackOut'))
  const isBlur = computed<boolean>(() => hasAny('hasBlur'))
  const showWaitAnimation = computed<boolean>(() => hasAny('hasWaitAnimation'))
  const isScrollLock = computed<boolean>(() => hasAny('hasScrollLock'))

  // スクロールロックは DOM 操作が必要なため、合成結果が変わったときだけ body に反映する。
  // 取得・解除の直後に反映されるよう同期で実行する
  watch(isScrollLock, (newValue) => {
    if (newValue) {
      disableBodyScroll(document.body)
    } else {
      enableBodyScroll(document.body)
    }
  }, { flush: 'sync' })

  /**
   * ロックを取得する
   * @param option 表示効果（未指定の項目は false）
   * @param type ロックの種類。'conditional' は useConditionalScreenLock 専用
   * @return 取得したロックを手放す関数（releaseLock）。何度呼んでも結果は同じ
   * @description releaseLock は自分の分を手放すだけで、他にロックがあれば画面は解除されない
   */
  const acquireLock = (option: ScreenLockOption = {}, type: ScreenLockType = 'manual'): () => void => {
    const owner = Symbol('screenLock')

    activeLocks.set(owner, {
      option: {
        hasBlackOut: !!option.hasBlackOut,
        hasBlur: !!option.hasBlur,
        hasWaitAnimation: !!option.hasWaitAnimation,
        hasScrollLock: !!option.hasScrollLock,
      },
      type,
    })

    return () => {
      activeLocks.delete(owner)
    }
  }

  /**
   * 手動で取得したロックをすべて手放す
   * @description
   *  bfcache 復帰時専用。遷移前提で解除されないロック（フォーム送信・一覧の絞り込み遷移等）や、遷移時に通信中だった API のロックが残るため外す。
   *  条件連動のロックは復元された状態（開いたままのモーダル・ドロワー等）と一致しているため外さない。
   */
  const releaseManualLocks = (): void => {
    activeLocks.forEach((lock, owner) => {
      if (lock.type === 'manual') activeLocks.delete(owner)
    })
  }

  // bfcache から復帰すると、遷移前に取得したまま手放されないロックが残り、画面を操作できなくなる。
  // ロックの取得より前に必ず store が生成されるため、ここで登録すれば取りこぼしはない
  const onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) releaseManualLocks()
  }

  window.addEventListener('pageshow', onPageShow)

  // store が破棄されたら（$dispose。テストで pinia を作り直す場合等）リスナーも外す
  onScopeDispose(() => {
    window.removeEventListener('pageshow', onPageShow)
  })

  return {
    // computed
    isLock,
    isBlackOut,
    isBlur,
    showWaitAnimation,

    // methods
    acquireLock,
    releaseManualLocks,
  }
})
